import React, { useRef } from "react"

import { Field, Form, Formik } from "formik"
import * as Yup from "yup"

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Textarea,
  useTheme,
} from "@chakra-ui/core"

import readFile from "src/readFile"

import { gql, useClient, useMutation } from "hooks/urql"

import {
  FileField,
  handleFailure,
} from "components/Forms/Formik/hookComponents"

export type SendMessageProps = {
  threadId: string
}

const ValidationSchema = Yup.object().shape({
  content: Yup.string().required("Message body required"),
})

const SendMessage: React.FC<SendMessageProps> = ({ threadId }) => {
  const theme: any = useTheme()
  const client = useClient()
  const formState = { content: "", threadId }
  const fileField = useRef<any>()
  const [, runMutation]: any[] = useMutation(createMessageMutation, client)

  const handleSubmit = (values, actions) => {
    const params = { ...values }
    readFile(values.attachment)
      .then(fileResult => {
        params.attachment = fileResult
        runMutation(params)
          .then(result => {
            const { failures } = result.data.createMessage

            if (failures.length > 0) {
              handleFailure(actions, failures)
            } else {
              actions.resetForm()
              if (fileField.current) fileField.current.value = ""
              actions.setSubmitting(false)
            }
          })
          .catch(() => handleFailure(actions))
      })
      .catch(() => handleFailure(actions))
  }

  return (
    <div className="mb-10">
      <Formik
        initialValues={formState}
        onSubmit={handleSubmit}
        validationSchema={ValidationSchema}
      >
        {({ status, isSubmitting }) => (
          <Form>
            <Field name="content">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    !!form.touched[field.name] && !!form.errors[field.name]
                  }
                  mt="4"
                >
                  <Flex
                    align={{ md: "center" }}
                    direction={["column", "column", "row"]}
                  >
                    <Textarea
                      id={field.name}
                      {...field}
                      flexBasis="50%"
                      placeholder="Send a message..."
                      autoFocus
                    />
                  </Flex>
                  <FormErrorMessage>{form.errors[field.name]}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name="attachment">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    !!form.touched[field.name] && !!form.errors[field.name]
                  }
                  mt="4"
                >
                  <FormLabel htmlFor={field.name}>Attach a File</FormLabel>
                  <Flex
                    align={{ md: "center" }}
                    direction={["column", "column", "row"]}
                  >
                    <div className="block">
                      <FileField name="attachment" ref={fileField} />
                    </div>
                  </Flex>
                  <FormErrorMessage>{form.errors[field.name]}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Flex align="center" mt="4">
              <Button
                type="submit"
                isLoading={isSubmitting}
                {...theme.buttonVariants.cloudroom}
                loadingText="Saving..."
                size="sm"
              >
                Send Message
              </Button>
              {status && (
                <Box bg="red.500" color="white" p="4" ml="8" rounded="md">
                  {status}
                </Box>
              )}
            </Flex>
          </Form>
        )}
      </Formik>
    </div>
  )
}

const createMessageMutation = gql`
  mutation CreateMessage(
    $threadId: ID!
    $content: String!
    $attachment: FileInputObject
  ) {
    createMessage(
      messageThreadId: $threadId
      content: $content
      attachment: $attachment
    ) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`

export default SendMessage
